/**
 * Utility functions for image processing
 */

/**
 * Resize an image file to fit within max dimensions while maintaining aspect ratio
 * @param {File} file - The image file to resize
 * @param {number} maxWidth - Maximum width (default 500px)
 * @param {number} maxHeight - Maximum height (default 500px)
 * @returns {Promise<{base64: string, blob: Blob, width: number, height: number}>}
 */
export const resizeImage = (file, maxWidth = 500, maxHeight = 500) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob and base64
        canvas.toBlob((blob) => {
          const base64 = canvas.toDataURL(file.type);
          resolve({
            base64,
            blob,
            width,
            height
          });
        }, file.type, 0.9); // 90% quality
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Validate image file
 * @param {File} file - The file to validate
 * @returns {{valid: boolean, error?: string}}
 */
export const validateImageFile = (file) => {
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  const maxSize = 1024 * 1024; // 1MB
  
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }
  
  if (!validTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: 'Only PNG, JPG, and JPEG formats are supported. SVG and other formats are not currently supported due to processing limitations.' 
    };
  }
  
  if (file.size > maxSize) {
    return { valid: false, error: 'File too large. Maximum size is 1MB' };
  }
  
  return { valid: true };
};

/**
 * Generate synthetic upload progress
 * @param {function} onProgress - Progress callback (0-100)
 * @param {number} fileSize - File size in bytes
 * @returns {{start: function, complete: function}}
 */
export const createProgressTracker = (onProgress, fileSize) => {
  let interval;
  let progress = 0;
  
  // Estimate upload time based on file size (assume 100KB/s)
  const estimatedTime = Math.max(2000, Math.min(10000, fileSize / 100));
  const increment = 100 / (estimatedTime / 100);
  
  return {
    start: () => {
      // Initial validation progress
      onProgress(10);
      
      // Simulated upload progress
      interval = setInterval(() => {
        progress += increment;
        if (progress < 90) {
          onProgress(Math.round(progress));
        }
      }, 100);
    },
    
    complete: () => {
      if (interval) {
        clearInterval(interval);
      }
      onProgress(100);
    }
  };
};